/**
 * useFeatureFlags Hook
 * Fetches and manages feature flags from backend API
 * Automatically refreshes on organization change
 */

import { useState, useEffect, useCallback } from 'react';
import sessionService from '../utils/sessionService.js';

import { API_BASE_URL } from '../utils/frontendConfig.js';

/**
 * Hook to fetch and manage feature flags
 * @param {string} organizationId - Current organization ID
 * @returns {Object} { flags, loading, error, refetch }
 */
const useFeatureFlags = (organizationId) => {
  const [flags, setFlags] = useState({
    adminProjectCreation: false,
    aiFeatures: false,
    analyticsAccess: false,
    billingAccess: false,
    bulkOperations: false,
    exportFeatures: false,
    integrationsAccess: false,
    userRole: null,
    organizationId: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFeatureFlags = useCallback(async () => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      // Use the actual backend permissions endpoint
      const token = sessionService.getSessionToken?.();
      const url = new URL(`${API_BASE_URL}/api/v1/users/permissions`);
      url.searchParams.append('organization_id', organizationId);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Failed to fetch feature flags: ${response.status} ${errText}`);
      }

      const data = await response.json();
      console.log('✅ Permissions loaded:', data);

      // Map permissions payload to feature flags shape
      setFlags({
        adminProjectCreation: !!data?.permissions?.can_create_projects,
        aiFeatures: false,
        analyticsAccess: !!data?.permissions?.can_view_all_projects,
        billingAccess: false,
        bulkOperations: false,
        exportFeatures: false,
        integrationsAccess: false,
        userRole: data?.role || null,
        organizationId: data?.organization_id || organizationId
      });
    } catch (err) {
      console.error('❌ Failed to fetch feature flags:', err);
      setError(err.message);
      // Set default flags on error
      setFlags({
        adminProjectCreation: false,
        aiFeatures: false,
        analyticsAccess: false,
        billingAccess: false,
        bulkOperations: false,
        exportFeatures: false,
        integrationsAccess: false,
        userRole: null,
        organizationId: null
      });
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  // Fetch on mount and when organization changes
  useEffect(() => {
    fetchFeatureFlags();
  }, [fetchFeatureFlags]);

  // Refetch function for manual refresh (e.g., after permission changes)
  const refetch = useCallback(() => {
    fetchFeatureFlags();
  }, [fetchFeatureFlags]);

  return {
    flags,
    loading,
    error,
    refetch
  };
};

/**
 * Check if admin project creation is enabled
 * Quick helper for common use case
 */
export const useAdminProjectCreation = (organizationId) => {
  const { flags, loading, error } = useFeatureFlags(organizationId);
  
  return {
    enabled: flags.adminProjectCreation,
    loading,
    error,
    userRole: flags.userRole
  };
};

export default useFeatureFlags;
